import { useContext, useEffect, useMemo, useState } from 'preact/hooks';
import { createContext, FunctionComponent } from 'preact';

import { subscribe, getSnapshot } from '../../../config/paywall/spid';
import { useSchibstedAccount } from '../../../utils/SchibstedAccountProvider';

export interface Geolocation {
  eea?: boolean;
  local?: boolean;
  se?: boolean;
  no?: boolean;
}

interface GeolocationContextProps {
  location: Geolocation;
}

const GeolocationContext = createContext<GeolocationContextProps>(undefined!);

export const GeolocationProvider: FunctionComponent = ({ children }) => {
  const [location, setLocation] = useState<{ eea?: boolean; local?: boolean }>(
    // Initialise from the snapshot in case location already resolved before mount.
    () => getSnapshot().location
  );

  const { getLocation } = useSchibstedAccount();

  useEffect(() => {
    // Kick off the location fetch (no-op if already in progress / resolved).
    getLocation().then(setLocation);
  }, []);

  // Also subscribe to snapshot updates so that a late-resolving location
  // (after `getLocation()` already returned `{}`) is picked up reactively.
  useEffect(() => {
    return subscribe(() => {
      const snap = getSnapshot();
      if (
        snap.location.eea !== undefined ||
        snap.location.local !== undefined
      ) {
        setLocation(snap.location);
      }
    });
  }, []);

  const state = useMemo(() => {
    return {
      location,
    };
  }, [location]);

  return (
    <GeolocationContext.Provider value={state}>
      {children}
    </GeolocationContext.Provider>
  );
};

export const useGeolocation = () => useContext(GeolocationContext);
